import React, { useEffect, useRef } from "react";

let mapsPromise = null;
function loadMaps() {
  if (mapsPromise) return mapsPromise;
  if (window.google?.maps?.Map) { mapsPromise = Promise.resolve(window.google.maps); return mapsPromise; }
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  // Google's official async bootstrap loader (Dynamic Library Import) — current best
  // practice: loads with loading=async and exposes google.maps.importLibrary().
  ((g) => {
    let h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary",
      q = "__ib__", m = document, b = window;
    b = b[c] || (b[c] = {});
    const d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(),
      u = () => h || (h = new Promise(async (f, n) => {
        a = m.createElement("script");
        e.set("libraries", [...r] + "");
        for (k in g) e.set(k.replace(/[A-Z]/g, (t) => "_" + t[0].toLowerCase()), g[k]);
        e.set("callback", c + ".maps." + q);
        a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
        d[q] = f;
        a.onerror = () => (h = n(Error(p + " could not load.")));
        a.nonce = m.querySelector("script[nonce]")?.nonce || "";
        m.head.append(a);
      }));
    d[l] ? console.warn(p + " only loads once. Ignoring:", g)
      : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
  })({ key, v: "weekly" });

  mapsPromise = window.google.maps.importLibrary("maps")
    .then(() => window.google.maps)
    .catch((err) => { mapsPromise = null; throw err; });
  return mapsPromise;
}

export function MapView({ facilities, activeId, center, radiusMiles, onSelect, onHover }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const circleRef = useRef(null);
  const [error, setError] = React.useState(false);

  useEffect(() => {
    let cancelled = false;
    loadMaps().then((maps) => {
      if (cancelled || !ref.current) return;
      try {
        if (!maps || typeof maps.Map !== "function") throw new Error("Google Maps unavailable");
        if (!mapRef.current) {
          mapRef.current = new maps.Map(ref.current, {
            center: { lat: 39.74, lng: -104.99 },
            zoom: 10,
            disableDefaultUI: true,
            zoomControl: true,
            styles: [
              { featureType: "poi", stylers: [{ visibility: "off" }] },
              { featureType: "transit", stylers: [{ visibility: "off" }] },
              { elementType: "labels.text.fill", stylers: [{ color: "#5C6660" }] },
              { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#F2F1EC" }] },
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#CBDAD2" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
            ],
          });
        }
        renderMarkers(maps);
      } catch (e) {
        console.warn("Map init failed:", e?.message);
        setError(true);
      }
    }).catch(() => setError(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const renderMarkers = (maps) => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(markersRef.current).forEach((m) => m.setMap(null));
    markersRef.current = {};
    const bounds = new maps.LatLngBounds();
    let count = 0;

    class PinOverlay extends maps.OverlayView {
      constructor(facility) { super(); this.facility = facility; this.div = null; }
      onAdd() {
        const beds = this.facility.available_beds;
        const isFree = !this.facility.tier || this.facility.tier === "free";
        const div = document.createElement("div");
        const label = isFree ? "•" : (beds > 0 ? `${beds}` : (beds === 0 ? "0" : "•"));
        div.className = `cb-pin ${beds > 0 && !isFree ? "available" : ""} ${this.facility.tier === "premium" ? "premium" : ""}`;
        div.textContent = label;
        div.title = this.facility.name;
        div.setAttribute("role", "button");
        div.setAttribute("tabindex", "0");
        const bedsText = isFree ? "Contact for availability" : (beds > 0 ? `${beds} beds available` : (beds === 0 ? "No beds available" : "Availability unknown"));
        div.setAttribute("aria-label", `${this.facility.name}, ${bedsText}`);
        const activate = () => onSelect(this.facility);
        div.addEventListener("click", activate);
        div.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); activate(); }
        });
        div.addEventListener("mouseenter", () => onHover?.(this.facility.facility_id));
        div.addEventListener("mouseleave", () => onHover?.(null));
        this.div = div;
        this.getPanes().overlayMouseTarget.appendChild(div);
      }
      draw() {
        if (!this.div) return;
        const p = this.getProjection().fromLatLngToDivPixel(
          new maps.LatLng(this.facility.lat, this.facility.lng));
        if (p) { this.div.style.left = p.x + "px"; this.div.style.top = p.y + "px"; this.div.style.position = "absolute"; }
      }
      onRemove() { if (this.div) { this.div.remove(); this.div = null; } }
      setActive(a) { if (this.div) this.div.classList.toggle("active", a); }
    }

    facilities.forEach((f) => {
      if (typeof f.lat !== "number") return;
      const ov = new PinOverlay(f);
      ov.setMap(map);
      markersRef.current[f.facility_id] = ov;
      bounds.extend(new maps.LatLng(f.lat, f.lng));
      count++;
    });

    if (circleRef.current) { circleRef.current.setMap(null); circleRef.current = null; }
    if (center && radiusMiles) {
      circleRef.current = new maps.Circle({
        map, center: { lat: center.lat, lng: center.lng }, radius: radiusMiles * 1609.34,
        strokeColor: "#0077B6", strokeOpacity: 0.6, strokeWeight: 2,
        fillColor: "#0077B6", fillOpacity: 0.06,
      });
      map.fitBounds(circleRef.current.getBounds(), 40);
    } else if (count > 0) {
      map.fitBounds(bounds, 60);
      if (count === 1) map.setZoom(13);
    }
  };

  useEffect(() => {
    if (error) return;
    if (window.google?.maps && mapRef.current) renderMarkers(window.google.maps);
    // eslint-disable-next-line
  }, [facilities, center, radiusMiles]);

  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, ov]) => ov.setActive?.(id === activeId));
  }, [activeId]);

  if (error) {
    return (
      <div data-testid="map-view" className="w-full h-full bg-[#E3F3ED] flex items-center justify-center p-8">
        <div className="max-w-sm text-center">
          <div className="mx-auto h-12 w-12 rounded-xl bg-white flex items-center justify-center mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1A365D" strokeWidth="2"><path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0-6 0"/><path d="M17.657 16.657L13.414 20.9a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0z"/></svg>
          </div>
          <h3 className="font-heading text-lg font-medium text-[#1C211F] mb-1">Map temporarily unavailable</h3>
          <p className="text-sm text-[#4A534E]">Enable the <span className="font-medium">Maps JavaScript API</span> for your Google Maps key to view facilities on the map. The list on the left works as usual.</p>
        </div>
      </div>
    );
  }

  return <div ref={ref} data-testid="map-view" className="w-full h-full bg-[#E8E6DF]" />;
}
